import { useState, VFC } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { feedback, pendingFeedback } from 'src/api/feedback'
import {
  FEEDBACK_EXCELLENT_ICON,
  FEEDBACK_FAIR_ICON,
  FEEDBACK_GOOD_ICON,
  FEEDBACK_NOT_INTEREST_ICON,
  FEEDBACK_PENDING_ICON,
} from 'src/assets/images'
import { FeedbackHasViolationIcon } from 'src/assets/svgs'
import { UserIcon } from 'src/components/Image/UserIcon'
import { TooltipWrapper } from 'src/components/Tooltip'
import { SmallButton } from 'src/elements/Button'
import { useErrorMessageModal } from 'src/hooks/useModal'
import { FeedbackRoom } from 'src/models/Room'
import {
  _feedback_has_violation,
  _feedback_modal_impression_score_bg,
  _feedbacksecondary,
  _menuicon,
  _messagebutton,
  _messagebuttonbg,
  cautionRed,
  mediumGray,
} from 'src/styles/colors'
import { fontWeightBold } from 'src/styles/font'
import styled, { css } from 'styled-components'

type FeedbackCardProps = {
  item: FeedbackRoom
  mutate: () => void
  isTab?: boolean
  onClickProfile?: (userId: number) => void
}

type UserInfoCounter = FeedbackRoom['counter']

const UserInfoContent: VFC<{ counter: UserInfoCounter }> = ({ counter }) => (
  <>
    <FlexCenter>
      <UserIcon src={counter.iconSrc} size="44px" />
    </FlexCenter>
    {counter.isPaidPlan && (
      <FlexCenter>
        <PaidLabel>有料プラン</PaidLabel>
      </FlexCenter>
    )}
    <p>{counter.companyName}</p>
    <p>
      {counter.name}
      {counter.position && ` / ${counter.position}`}
    </p>
  </>
)
type ImpressionScoreType =
  | 'excellent'
  | 'good'
  | 'fair'
  | 'pending'
  | 'not_interest'
  | ''

const HasViorationReasons = [
  { key: 'one_way_sales', label: '一方的な営業行為' },
  { key: 'unscheduled_cancel', label: '無断キャンセル' },
  { key: 'lateness', label: '遅刻' },
  { key: 'different_person', label: '別の担当者だった' },
]
type FeedbackFormData = {
  content: string
}

const FeedbackCardComponent: VFC<FeedbackCardProps> = ({
  item,
  mutate,
  isTab = false,
  onClickProfile,
}) => {
  const [impressionScore, setImpressionScore] =
    useState<ImpressionScoreType>('')
  const [hasViolations, setHasViolations] = useState<boolean>(false)
  const [selectedReasons, setSelectedReasons] = useState<string[]>([])
  const [isRequesting, setIsRequesting] = useState<boolean>(false)

  const methods = useForm<FeedbackFormData>()
  const { register, handleSubmit } = methods

  const handleCheckboxChange = (key: string) => {
    if (selectedReasons.includes(key))
      setSelectedReasons(selectedReasons.filter((r) => r !== key))
    else setSelectedReasons([...selectedReasons, key])
  }
  const { open } = useErrorMessageModal()

  const submit = handleSubmit(async (data) => {
    if (hasViolations && selectedReasons.length === 0) {
      open({ message: '違反事項がある場合は理由を選択してください' })
      return
    }
    setIsRequesting(true)
    if (
      impressionScore === 'excellent' ||
      impressionScore === 'good' ||
      impressionScore === 'fair'
    ) {
      const res = await feedback({
        userId: item.counter.id,
        roomId: item.id,
        roomType: item.roomType,
        impressionScore: impressionScore,
        hasViolations,
        feedbackViolationReasons: hasViolations ? selectedReasons : null,
        content: data.content,
      })
      if (res.error) {
        open({ message: res.error.errorMessages?.join('\n') })
        return
      }
    } else if (
      impressionScore === 'pending' ||
      impressionScore === 'not_interest'
    )
      pendingFeedback({
        userId: item.counter.id,
        roomId: item.id,
        roomType: item.roomType,
        status: impressionScore,
      })
    mutate()
    setIsRequesting(false)
  })

  return (
    <FeedbackCardDiv>
      {onClickProfile ? (
        <UserInfoButton
          type="button"
          onClick={() => onClickProfile(item.counter.id)}
        >
          <UserInfoContent counter={item.counter} />
        </UserInfoButton>
      ) : (
        <UserInfo>
          <UserInfoContent counter={item.counter} />
        </UserInfo>
      )}
      <SentAt>
        <p>
          メッセージ開始日時:{' '}
          {item.firstMessageSentAt.format('YYYY/MM/DD HH:mm')}
        </p>
      </SentAt>
      <FormProvider {...methods}>
        <FeedbackForm onSubmit={submit}>
          <BeFeedbackDiv>
            <p>相手の印象</p>
            <ImpressionScoreWrapper isTab={isTab}>
              <ImpressionScoreDiv
                impressionScore={impressionScore}
                checked={impressionScore === 'excellent'}
                isTab={isTab}
              >
                <ImpressionScoreLabel isTab={isTab}>
                  <HiddenRadio
                    name="impressionScore"
                    type="radio"
                    checked={impressionScore === 'excellent'}
                    onChange={() => setImpressionScore('excellent')}
                  />
                  <img
                    src={FEEDBACK_EXCELLENT_ICON}
                    alt=""
                    height={25}
                    width={25}
                  />
                  <p>とても良かった</p>
                </ImpressionScoreLabel>
              </ImpressionScoreDiv>
              <ImpressionScoreDiv
                impressionScore={impressionScore}
                checked={impressionScore === 'good'}
                isTab={isTab}
              >
                <ImpressionScoreLabel isTab={isTab}>
                  <HiddenRadio
                    name="impressionScore"
                    type="radio"
                    onChange={() => setImpressionScore('good')}
                  />
                  <img src={FEEDBACK_GOOD_ICON} alt="" height={25} width={25} />
                  <p>良かった</p>
                </ImpressionScoreLabel>
              </ImpressionScoreDiv>
              <ImpressionScoreDiv
                impressionScore={impressionScore}
                checked={impressionScore === 'fair'}
                isTab={isTab}
              >
                <ImpressionScoreLabel isTab={isTab}>
                  <HiddenRadio
                    name="impressionScore"
                    type="radio"
                    onChange={() => setImpressionScore('fair')}
                  />
                  <img src={FEEDBACK_FAIR_ICON} alt="" height={25} width={25} />
                  <p>悪かった</p>
                </ImpressionScoreLabel>
              </ImpressionScoreDiv>
              <ImpressionScoreDiv
                impressionScore={impressionScore}
                checked={impressionScore === 'pending'}
                isTab={isTab}
              >
                <ImpressionScoreLabel isTab={isTab}>
                  <HiddenRadio
                    name="impressionScore"
                    type="radio"
                    onChange={() => setImpressionScore('pending')}
                  />
                  <img
                    src={FEEDBACK_PENDING_ICON}
                    alt=""
                    height={25}
                    width={25}
                  />
                  <p>まだ会ってない</p>
                </ImpressionScoreLabel>
              </ImpressionScoreDiv>
              <ImpressionScoreDiv
                impressionScore={impressionScore}
                checked={impressionScore === 'not_interest'}
                isTab={isTab}
              >
                <ImpressionScoreLabel isTab={isTab}>
                  <HiddenRadio
                    name="impressionScore"
                    type="radio"
                    onChange={() => setImpressionScore('not_interest')}
                  />
                  <img
                    src={FEEDBACK_NOT_INTEREST_ICON}
                    alt=""
                    height={25}
                    width={25}
                  />
                  <p>会う予定がない</p>
                </ImpressionScoreLabel>
              </ImpressionScoreDiv>
            </ImpressionScoreWrapper>
            <>
              <TooltipWrapper
                direction={{ y: 'top' }}
                xValue={isTab ? '20px' : '250px'}
                yValue={isTab ? '20px' : '30px'}
                tooltipNode={
                  <label>
                    以下の事項が違反行為に該当します
                    <br />
                    ・一方的な営業行為
                    <br />
                    ・無断キャンセル
                    <br />
                    ・遅刻
                    <br />
                    ・別の担当者だった
                  </label>
                }
              >
                <FeedbackHasViolationTitle>
                  違反事項があった
                  <FeedbackHasViolationIconDiv>
                    <FeedbackHasViolationIcon />
                  </FeedbackHasViolationIconDiv>
                </FeedbackHasViolationTitle>
              </TooltipWrapper>
              <HasViolationDiv>
                <HasViolationTextDiv>
                  <Label4 checked={hasViolations === false}>
                    <HiddenRadio
                      name="hasViolations"
                      type="radio"
                      onChange={() => setHasViolations(false)}
                    />
                    いいえ
                  </Label4>
                </HasViolationTextDiv>
                <HasViolationTextDiv>
                  <Label5 checked={hasViolations === true}>
                    <HiddenRadio
                      name="hasViolations"
                      type="radio"
                      onChange={() => setHasViolations(true)}
                    />
                    はい
                  </Label5>
                </HasViolationTextDiv>
              </HasViolationDiv>
              {hasViolations && (
                <HasViolationReasonsDiv isTab={isTab}>
                  <p>詳細を教えてください（複数選択可）</p>
                  {HasViorationReasons.map((reason) => (
                    <div key={reason.key}>
                      <label>
                        <input
                          type="checkbox"
                          value={reason.key}
                          checked={selectedReasons.includes(reason.key)}
                          onChange={() => handleCheckboxChange(reason.key)}
                        />
                        {reason.label}
                      </label>
                    </div>
                  ))}
                </HasViolationReasonsDiv>
              )}
            </>
            <ContentAreaTitle>コメントを入力（任意）</ContentAreaTitle>
            <ContentArea isTab={isTab}>
              <textarea
                placeholder={`・担当者が出てきた\n・提案の質が高かったまたは低かった\nなどアポイント時に感じたことをご記載ください。\n※相手には匿名で送られます。`}
                {...register('content')}
              />
            </ContentArea>
          </BeFeedbackDiv>
          <FlexCenter>
            <SubmitButton disabled={impressionScore === '' || isRequesting}>
              評価を確定する
            </SubmitButton>
          </FlexCenter>
        </FeedbackForm>
      </FormProvider>
    </FeedbackCardDiv>
  )
}
export const FeedbackCard = styled(FeedbackCardComponent)``

const FlexCenter = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 8px;
`

const userInfoTextStyles = css`
  p {
    text-align: center;
    margin-top: 4px;
    font-size: 16px;
    font-weight: ${fontWeightBold};
  }
`

const UserInfo = styled.div`
  ${userInfoTextStyles}
`

const UserInfoButton = styled.button`
  display: block;
  width: 100%;
  padding: 0;
  border: none;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: center;
  cursor: pointer;
  ${userInfoTextStyles}
`
const SentAt = styled.p`
  margin-top: 12px;
  text-align: center;
  font-size: 12px;
`
const FeedbackForm = styled.form`
  font-size: 12px;
  p {
    font-weight: ${fontWeightBold};
  }
  > div {
    margin-top: 12px;
  }
`
const FeedbackCardDiv = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px 64px;
  box-shadow: 0px 5px 5px 5px #9c9da099;
  border-radius: 5px;

  > div,
  form {
    :not(:first-child) {
      margin-top: 12px;
    }
  }
  textarea {
    margin-top: 8px;
    width: 100%;
    height: 80px;
    resize: none;
    border: 1px solid ${_feedbacksecondary};
    border-radius: 5px;
    padding: 8px;
    text-align: left;
  }
`

const PaidLabel = styled.label`
  border-radius: 5px;
  font-weight: ${fontWeightBold};

  text-align: center;
  padding: 4px 8px;

  color: ${_messagebutton};
  background-color: ${_messagebuttonbg};
  border: 1px solid ${_messagebuttonbg};
`

const HiddenRadio = styled.input`
  display: none;
`

const SubmitButton = styled(SmallButton)`
  margin-top: 8px;
  padding: 6px 12px;
  color: ${_messagebutton};
  background-color: ${_messagebuttonbg};

  :disabled {
    opacity: 50%;
    cursor: not-allowed;
  }
`

const ImpressionScoreWrapper = styled.div<{ isTab: boolean }>`
  display: flex;
  justify-content: center;
  ${({ isTab }) =>
    isTab &&
    css`
      flex-direction: column;
      margin: 8px 0px 20px;
    `}
`
const FeedbackHasViolationTitle = styled.p`
  display: flex;
  justify-content: center;
`
const FeedbackHasViolationIconDiv = styled.div`
  align-items: center;
  margin-left: 8px;
  padding-top: 1px;
`

const BeFeedbackDiv = styled.div`
  border-top: 2px solid ${_menuicon};
  margin-top: 12px;
  padding-top: 12px;

  p {
    text-align: center;
    font-size: 14px;
    font-weight: ${fontWeightBold};
  }
`

const ImpressionScoreDiv = styled.div<{
  impressionScore: ImpressionScoreType
  checked: boolean
  isTab: boolean
}>`
  display: flex;
  flex-direction: column;
  min-width: 100px;
  ${({ isTab }) =>
    isTab
      ? css`
          margin: 0 0 0 40px;
        `
      : css`
          margin: 16px 2px;
          align-items: center;
        `}
  padding: 8px 8px;

  display: inline-flex;
  justify-content: center;
  border-radius: 12px;
  font-size: 12px;
  text-align: center;

  // NOTE: checked時に要素の位置を動かさないために設定
  border: 2px solid white;
  ${({ impressionScore, checked }) =>
    checked
      ? impressionScore === 'excellent'
        ? css`
            background-color: ${_feedback_modal_impression_score_bg};
            border: 2px solid ${cautionRed};
          `
        : impressionScore === 'good'
        ? css`
            background-color: ${_feedback_modal_impression_score_bg};
            border: 2px solid ${cautionRed};
          `
        : impressionScore === 'fair'
        ? css`
            background-color: ${_feedback_modal_impression_score_bg};
            border: 2px solid ${cautionRed};
          `
        : impressionScore === 'pending'
        ? css`
            background-color: ${_feedback_modal_impression_score_bg};
            border: 2px solid ${cautionRed};
          `
        : impressionScore === 'not_interest'
        ? css`
            background-color: ${_feedback_modal_impression_score_bg};
            border: 2px solid ${cautionRed};
          `
        : css``
      : css``}
`

const ImpressionScoreLabel = styled.label<{ isTab: boolean }>`
  text-align: center;
  align-items: center;
  cursor: pointer;
  ${({ isTab }) =>
    isTab &&
    css`
      display: flex;

      img {
        margin-right: 8px;
      }
    `}
  p {
    text-align: center;
    cursor: pointer;
  }
`

const HasViolationDiv = styled.div`
  display: flex;
  justify-content: center;
`

const HasViolationTextDiv = styled.div`
  margin: 14px 0;
  text-align: center;
`

const hasViolationlCss = css<{ checked: boolean }>`
  padding: 6px 32px;
  margin: 0 16px;
  border-radius: 20px;
  font-weight: ${fontWeightBold};
  color: white;
  text-align: center;
  white-space: nowrap;
  cursor: pointer;
  display: inline-block;
  width: 100px;

  ${(props) => (props.checked ? `opacity: 0.9;` : 'opacity: 0.5;')}

  &:hover {
    opacity: 1;
  }
`

const Label4 = styled.label`
  ${hasViolationlCss}
  background-color: ${mediumGray};
`

const Label5 = styled.label`
  ${hasViolationlCss}
  background-color: ${_feedback_has_violation};
`

const HasViolationReasonsDiv = styled.div<{ isTab: boolean }>`
  margin: 20px 0;
  text-align: center;

  p {
    margin-bottom: 12px;
  }

  /* チェックボックスのラベル */
  label {
    display: flex;
    padding-top: 12px;
    ${({ isTab }) =>
      isTab
        ? css`
            padding-left: 30px;
          `
        : css`
            padding-left: 120px;
          `}
    cursor: pointer;
  }

  /* チェックボックスをカスタマイズ */
  input[type='checkbox'] {
    appearance: none; /* デフォルトのチェックボックスのスタイルを無効化 */
    width: 20px;
    height: 20px;
    border: 2px solid #000;
    border-radius: 3px;
    position: relative;
    margin-right: 10px;
    cursor: pointer;
  }

  /* チェックボックスがチェックされた場合 */
  input[type='checkbox']:checked {
    background-color: #000;
    border-color: #000;
  }

  input[type='checkbox']:checked::after {
    content: '';
    position: absolute;
    top: 2px;
    left: 6px;
    width: 5px;
    height: 10px;
    border: solid #fff;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
  }
`

const ContentAreaTitle = styled.p`
  text-align: center;
  font-size: 14px;
  padding-top: 12px;
`

const ContentArea = styled.div<{ isTab: boolean }>`
  display: flex;
  justify-content: center;

  ${({ isTab }) =>
    isTab &&
    css`
      textarea {
        width: 80%;
      }
    `}
`
